import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';
import MasterCatalogSidebar, { MasterCatalogDrawer, MasterCatalogMobileTrigger } from '../../components/masters/MasterCatalogPanel';
import MasterTypeHeader from '../../components/masters/MasterTypeHeader';
import MasterRecordTable from '../../components/masters/MasterRecordTable';
import MasterFormDrawer, { MasterFormState } from '../../components/masters/MasterFormDrawer';
import CountryFormDrawer from '../../components/masters/CountryFormDrawer';
import StateFormDrawer from '../../components/masters/StateFormDrawer';
import CityFormDrawer from '../../components/masters/CityFormDrawer';
import CurrencyFormDrawer from '../../components/masters/CurrencyFormDrawer';
import LanguageFormDrawer from '../../components/masters/LanguageFormDrawer';
import TimezoneFormDrawer from '../../components/masters/TimezoneFormDrawer';
import AddressFieldFormDrawer from '../../components/masters/AddressFieldFormDrawer';
import PaymentMethodFormDrawer from '../../components/masters/PaymentMethodFormDrawer';
import BankFormDrawer from '../../components/masters/BankFormDrawer';
import { useI18n } from '../../i18n/I18nProvider';
import StateDeleteDialog from '../../components/masters/StateDeleteDialog';
import StateListFilters, { type StateStatusFilter } from '../../components/masters/StateListFilters';
import CategoryChildrenDrawer from '../../components/masters/CategoryChildrenDrawer';
import { selectEnabledModules } from '../../store/slices/tenantSlice';
import { selectIsSuperAdmin } from '../../store/slices/authSlice';
import { filterSetupPhases, HIERARCHY_PARENT_TYPE, type MasterSetupPhase } from '../../constants/masterSetupFlow';
import {
  getEmbeddedExternalCatalogGroup,
  embeddedExternalMasterMeta,
  isEmbeddedExternalMaster,
  isEmbeddedExternalType,
} from '../../constants/commonExternalMasters';
import { filterMasterCatalog } from '../../utils/serviceMasterCatalog';
import PartnerSubscriptionPlanManager from '../../components/partners/PartnerSubscriptionPlanManager';
import { RestaurantsPage } from '../food/FoodPages';
import { LEGACY_PARENT_META_KEY, enrichMetadataFields, enrichFoodImageMetadataFields, normalizeMetadataFields } from '../../constants/masterRefFields';
import { resolveServiceSetupPhases } from '../../constants/serviceMasterSetupFlow';
import {
  getNextSequentialSortOrder,
  TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES,
  TRANSPORT_UNIQUE_SORT_ORDER_TYPES,
  TRANSPORT_STATUS_TOGGLE_TYPES,
  TRANSPORT_HIDE_STATUS_TOGGLE_TYPES,
  canDeleteTripTypeMaster,
  TRANSPORT_HARD_DELETE_TYPES,
  isMasterRecordDeprecated,
  enrichTransportVehicleMetadataFields,
  enrichTransportBrandMetadataFields,
  enrichTransportModelMetadataFields,
  enrichTransportVariantMetadataFields,
  enrichRideAttributeValueMetadataFields,
  enrichRideTagMetadataFields,
  enrichServiceAvailabilityScheduleMetadataFields,
  normalizeServiceAvailabilityDaysMetadata,
  syncServiceAvailabilityDaysForSave,
  resolveTransportMasterImageUrl,
} from '../../constants/transportSubserviceMasters';
import { FOOD_HIDE_STATUS_TOGGLE_TYPES, FOOD_LIST_STATUS_TOGGLE_TYPES, FOOD_SEQUENTIAL_SORT_ORDER_TYPES, FOOD_INTEGER_SORT_ORDER_TYPES, FOOD_UNIQUE_SORT_ORDER_TYPES, FOOD_STATUS_TOGGLE_LIST_TYPES, sortFoodSubCategoryRows, sortFoodAttributeValueRows, sortFoodMasterRowsBySortOrder, sortFoodAvailabilityScheduleRows, buildFoodAttributeOrderMap, enrichFoodTagMetadataFields, enrichFoodAttributeMetadataFields, enrichFoodAttributeValueMetadataFields, enrichFoodVariantMetadataFields, enrichAddonGroupMetadataFields, enrichFoodAddonMetadataFields, enrichPreparationTimeMetadataFields, enrichFoodUnitMetadataFields, enrichFoodTaxGroupMetadataFields, enrichKitchenStationMetadataFields, enrichFoodAvailabilityScheduleMetadataFields, enrichFoodImageMasterMetadataFields, formatPreparationTimeName, preparationTimeCodeFromMinutes, formatFoodTaxGroupName, normalizeFoodUnitType, normalizeKitchenStationType, getNextFoodMasterSortOrder, resolveRecordSortOrder } from '../../constants/foodMasterRules';
import {
  DELIVERY_STATUS_TOGGLE_LIST_TYPES,
  DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES,
  DELIVERY_INTEGER_SORT_ORDER_TYPES,
  DELIVERY_UNIQUE_SORT_ORDER_TYPES,
  DELIVERY_HIDE_STATUS_TOGGLE_TYPES,
  enrichDeliverySlotMetadataFields,
  enrichDeliveryPartnerPricingMetadataFields,
  enrichDeliveryIncentiveMetadataFields,
  enrichDeliveryPartnerTierMetadataFields,
  enrichMerchantDocumentMetadataFields,
  enrichCancellationReasonMetadataFields,
  enrichSubstitutionRuleMetadataFields,
  enrichPackagingTypeMetadataFields,
  enrichGroceryStoreTypeMetadataFields,
  enrichGroceryCategoryMetadataFields,
  enrichGrocerySubCategoryMetadataFields,
  enrichGroceryBrandMetadataFields,
  enrichWarehouseMetadataFields,
  enrichProductVariantMetadataFields,
  enrichWeightTypeMetadataFields,
  enrichSupplierMetadataFields,
  enrichStockAlertMetadataFields,
  enrichGroceryTaxGroupMetadataFields,
  enrichGroceryProductImageMetadataFields,
  enrichGroceryAvailabilityScheduleMetadataFields,
  resolveDeliveryServiceSlugDefault,
  getNextGrocerySubCategorySortOrder,
  resolveGrocerySubCategoryParentCode,
  DELIVERY_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES,
  sortDeliverySlotRows,
  sortDeliveryPartnerPricingRows,
  sortDeliveryIncentiveRows,
  sortDeliveryPartnerTierRows,
  sortMerchantDocumentRows,
  sortCancellationReasonRows,
  sortSubstitutionRuleRows,
  sortPackagingTypeRows,
  sortGroceryStoreTypeRows,
  sortGroceryCategoryRows,
  sortGrocerySubCategoryRows,
  sortGroceryBrandRows,
  sortWarehouseRows,
  sortProductVariantRows,
  sortWeightTypeRows,
  sortSupplierRows,
  sortStockAlertRows,
  sortGroceryTaxGroupRows,
  sortGroceryProductImageRows,
  sortGroceryAvailabilityScheduleRows,
} from '../../constants/deliveryMasterRules';
import { sortRowsByOpeningHourDay } from '../../utils/restaurantOpeningHours';
import { composeE164, splitPhoneByDialCodes } from '../../utils/phoneDialCode';
import { getCachedCountryDialCodes } from '../../services/countryDialCodeService';
import type { ServiceHubType } from '../../constants/moduleFlows';
import {
  ON_DEMAND_MASTER_HELPERS,
  ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES,
  ON_DEMAND_UNIQUE_SORT_ORDER_TYPES,
  ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES,
  type OnDemandMasterTypeKey,
} from '../../constants/onDemandMasters';
import {
  getMasterCatalog, getMasterSchema, getMasters, createMaster, updateMaster, deleteMaster,
  getMasterOptions, bulkImportMasters,
} from '../../services/platform/masterService';
import MasterImportDialog from '../../components/masters/MasterImportDialog';
import MasterContentErrorBoundary from '../../components/masters/MasterContentErrorBoundary';
import { downloadMasterCsv, masterCsvRowToPayload } from '../../utils/masterCsv';
import type { MasterCsvRow } from '../../utils/masterCsv';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PageShell from '../../components/layout/PageShell';

interface MasterRow {
  _id: string;
  code: string;
  name: string;
  description?: string;
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  parentId?: string | { _id: string; code?: string; name?: string };
  sortOrder?: number;
  metadata?: Record<string, unknown>;
  isActive?: boolean;
}

/** Master types that show an enable/disable switch in the list table */
const MASTER_TYPES_WITH_STATUS_TOGGLE = new Set([
  'countries',
  'currencies',
  'timezones',
  'taxes',
  'units',
  'rejection_reasons',
  'delivery_modes',
  'address_fields',
  'payment_methods',
  'banks',
  'payout_cycles',
  'transport_countries',
  'transport_cancellation_reasons',
  'languages',
  'service_categories',
  'skills',
  'certificates',
  'experience_levels',
  'pricing_templates',
  'visit_charges',
  'cancellation_reasons',
  ...TRANSPORT_STATUS_TOGGLE_TYPES,
]);


function resolveParentId(
  row: MasterRow,
  parentOptions: Array<{ _id: string; code: string }>,
  type: string,
): string {
  const raw = row.parentId;
  if (raw) {
    if (typeof raw === 'object' && raw !== null && '_id' in raw) {
      return String(raw._id);
    }
    return String(raw);
  }
  const legacyKey = LEGACY_PARENT_META_KEY[type];
  if (legacyKey) {
    const code = row.metadata?.[legacyKey];
    if (typeof code === 'string') {
      const found = parentOptions.find((p) => p.code === code);
      if (found) return found._id;
    }
  }
  return '';
}

const EMPTY_CATALOG: Awaited<ReturnType<typeof getMasterCatalog>> = [];
const EMPTY_PARENT_OPTIONS: Awaited<ReturnType<typeof getMasterOptions>> = [];

const emptyForm = (): MasterFormState => ({
  code: '', name: '', description: '', country: '', state: '', city: '', district: '',
  parentId: '', sortOrder: 0, isActive: true, metadata: {},
});

type MasterValidationRecord = {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
  parentId?: string;
  attributeCode?: string;
  parentCategoryCode?: string;
  metadata?: Record<string, unknown>;
  isActive?: boolean;
  description?: string;
};

function mapMasterRowForValidation(row: MasterRow): MasterValidationRecord {
  const meta = row.metadata || {};
  const rawParentId = row.parentId;
  const parentId = typeof rawParentId === 'object' && rawParentId?._id
    ? rawParentId._id
    : typeof rawParentId === 'string'
      ? rawParentId
      : undefined;
  return {
    _id: row._id,
    code: row.code,
    name: row.name,
    sortOrder: resolveRecordSortOrder({ sortOrder: row.sortOrder, metadata: meta }),
    parentId,
    attributeCode: meta.attributeCode != null ? String(meta.attributeCode) : undefined,
    parentCategoryCode: meta.parentCategoryCode != null ? String(meta.parentCategoryCode) : undefined,
    metadata: meta,
    isActive: row.isActive,
    description: row.description,
  };
}

function buildCreateFormState(
  type: string,
  schema: { metadataFields?: Array<{ key: string }> } | null | undefined,
  totalRows: number,
  existingRecords: Array<{ sortOrder?: number; metadata?: Record<string, unknown> }> = [],
  hubServiceSlug?: string,
): MasterFormState {
  const base = emptyForm();
  if (schema?.metadataFields?.some((f) => f.key === 'status')) {
    base.metadata = { ...base.metadata, status: 'active' };
  }
  if (type === 'languages') {
    base.metadata = { ...base.metadata, autoTranslate: true, rtl: false };
  }
  if (type === 'service_categories') {
    const nextOrder = getNextFoodMasterSortOrder(existingRecords);
    base.sortOrder = nextOrder;
    base.metadata = {
      ...base.metadata,
      status: 'active',
      sortOrder: nextOrder,
      color: '#0f766e',
    };
    base.isActive = true;
  }
  if (ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES.has(type) && type !== 'service_categories') {
    const nextOrder = getNextFoodMasterSortOrder(existingRecords);
    base.sortOrder = nextOrder;
    base.metadata = {
      ...base.metadata,
      status: 'active',
      sortOrder: nextOrder,
    };
    base.isActive = true;
  }
  if (FOOD_SEQUENTIAL_SORT_ORDER_TYPES.has(type)
    && type !== 'food_sub_categories'
    && type !== 'food_attribute_values') {
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  } else if (DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES.has(type)) {
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  } else if (TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES.has(type)) {
    base.sortOrder = getNextSequentialSortOrder(totalRows);
  }
  if (type === 'food_sub_categories' || type === 'food_attribute_values' || type === 'grocery_sub_categories') {
    base.sortOrder = 0;
  }
  if (type === 'addon_groups') {
    base.metadata = {
      ...base.metadata,
      minSelection: 0,
      maxSelection: 1,
      required: false,
    };
  }
  if (type === 'delivery_slots') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
      fee: 0,
      start: '07:00',
      end: '10:00',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'packaging_types') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
      fee: 0,
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'grocery_store_types') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
    };
    base.isActive = true;
    if (!base.sortOrder || base.sortOrder < 1) {
      base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
    }
  }
  if (type === 'grocery_brands') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'warehouses') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
    };
    base.isActive = true;
    base.country = base.country || 'IN';
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'product_variants') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'weight_types') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'suppliers') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
      phoneCountryCode: '+91',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'stock_alerts') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'grocery_tax_groups') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
      taxIncluded: false,
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'grocery_product_images') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
      imageType: 'thumbnail',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'grocery_availability_schedules') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
      startTime: '07:00',
      endTime: '22:00',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'grocery_categories') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'grocery_sub_categories') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
    };
    base.isActive = true;
    base.sortOrder = 0;
  }
  if (type === 'delivery_partner_pricing') {
    const serviceSlug = resolveDeliveryServiceSlugDefault(hubServiceSlug);
    base.metadata = {
      ...base.metadata,
      status: 'active',
      serviceSlug,
      baseKm: 3,
      baseAmount: 35,
      remainingKmRate: 6,
      pickupCharge: 5,
      dropCharge: 5,
      waitingChargePerMinute: 0,
      minimumGuarantee: 50,
      maximumDeliveryKm: 15,
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
    base.description = 'Base ₹35 for first 3 km · ₹6/km after · pickup ₹5 · drop ₹5 · min guarantee ₹50 · max 15 km';
  }
  if (type === 'delivery_incentives') {
    base.metadata = {
      ...base.metadata,
      serviceSlug: resolveDeliveryServiceSlugDefault(hubServiceSlug),
      incentiveType: 'peak_hour',
      startTime: '11:00',
      endTime: '14:00',
      amount: 20,
    };
  }
  if (type === 'delivery_partners_tiers') {
    base.metadata = {
      ...base.metadata,
      minOrders: 0,
      bonusPercent: 0,
    };
    base.description = 'Base tier — partners qualify from 0 completed orders';
  }
  if (type === 'merchant_document_types') {
    base.metadata = {
      ...base.metadata,
      status: 'active',
      category: 'compliance',
      required: true,
      identifierRequired: true,
      expiryRequired: false,
      entityTypes: 'merchant,vendor',
    };
    base.isActive = true;
    base.sortOrder = getNextFoodMasterSortOrder(existingRecords);
  }
  if (type === 'cancellation_reasons') {
    base.metadata = {
      ...base.metadata,
      appliesTo: 'customer',
    };
  }
  if (type === 'substitution_rules') {
    base.metadata = {
      ...base.metadata,
      allowSubstitute: true,
    };
  }
  return base;
}

export default function MasterDataPage({
  embedded = false,
  catalogScope = 'common',
  serviceHub,
  serviceSlug,
  restrictGroupKeys,
  setupPhasesOverride,
  singleTypeMode,
}: {
  embedded?: boolean;
  catalogScope?: 'common' | 'service';
  serviceHub?: string;
  serviceSlug?: string;
  /** When set, only these master groups appear in the catalog sidebar */
  restrictGroupKeys?: string[];
  setupPhasesOverride?: MasterSetupPhase[];
  pageTitle?: string;
  pageSubtitle?: string;
  /** Dedicated page: one master type only — no catalog sidebar or setup guide */
  singleTypeMode?: { groupKey: string; typeKey: string };
} = {}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeType = searchParams.get('type') || '';
  const activeGroup = searchParams.get('group') || '';
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [stateCountryFilter, setStateCountryFilter] = useState('');
  const [stateStatusFilter, setStateStatusFilter] = useState<StateStatusFilter>('all');
  const [cityStateFilter, setCityStateFilter] = useState('');
  const [saveToast, setSaveToast] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const countryFormInitKey = useRef<string | null>(null);
  const [editing, setEditing] = useState<MasterRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MasterRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [form, setForm] = useState<MasterFormState>(emptyForm());
  const [saveError, setSaveError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; failed: { code: string; error: string }[] } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [togglingRowId, setTogglingRowId] = useState<string | null>(null);
  const [partnerPlanAddRequest, setPartnerPlanAddRequest] = useState(0);
  const [restaurantAddRequest, setRestaurantAddRequest] = useState(0);
  const [langToast, setLangToast] = useState<string | null>(null);
  const [categoryChildrenParent, setCategoryChildrenParent] = useState<MasterRow | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const enabledModules = useSelector(selectEnabledModules);
  const { reloadLanguages } = useI18n();

  const { data: catalogRaw = EMPTY_CATALOG } = useQuery({
    queryKey: ['master-catalog', catalogScope, serviceHub, serviceSlug],
    queryFn: () => getMasterCatalog({ scope: catalogScope, hub: serviceHub, slug: serviceSlug }),
    staleTime: 5 * 60_000,
  });

  const effectiveRestrictGroupKeys = singleTypeMode
    ? [singleTypeMode.groupKey]
    : restrictGroupKeys;

  const catalog = useMemo(() => filterMasterCatalog(catalogRaw, {
    embedded,
    serviceHub,
    serviceSlug,
    enabledModules,
    isSuperAdmin,
    restrictGroupKeys: effectiveRestrictGroupKeys,
  }), [catalogRaw, enabledModules, isSuperAdmin, effectiveRestrictGroupKeys, embedded, serviceHub, serviceSlug]);

  const setupPhases = useMemo(() => {
    if (singleTypeMode) return [];
    if (setupPhasesOverride?.length) return setupPhasesOverride;
    if (embedded && serviceHub) {
      return resolveServiceSetupPhases(serviceHub as ServiceHubType, serviceSlug, catalog);
    }
    if (!embedded) return filterSetupPhases(enabledModules, isSuperAdmin);
    return [];
  }, [setupPhasesOverride, embedded, serviceHub, serviceSlug, catalog, enabledModules, isSuperAdmin]);

  const resolvedType = useMemo(() => {
    if (singleTypeMode) {
      return { type: singleTypeMode.typeKey, group: singleTypeMode.groupKey };
    }
    if (activeType === 'partner_subscriptions') {
      return { type: 'partner_subscription_plans', group: '_external' };
    }
    if (activeType && activeGroup === '_external') {
      return { type: activeType, group: activeGroup };
    }
    if (activeType) {
      const owningGroup = catalog.find((g) => g.types.some((t) => t.key === activeType));
      if (owningGroup) {
        return { type: activeType, group: owningGroup.key };
      }
      // Honor deep-linked URL when catalog filtering hides the type (e.g. module gating).
      return { type: activeType, group: activeGroup };
    }
    if (embedded && setupPhases.length) {
      const step = setupPhases.flatMap((p) => p.steps).find((s) => !s.externalLink);
      if (step) return { type: step.typeKey, group: step.groupKey };
    }
    const first = catalog[0]?.types[0];
    if (first) return { type: first.key, group: catalog[0].key };
    // Embedded hub masters must not fall back to global geo/countries
    if (embedded) return { type: '', group: '' };
    return { type: 'countries', group: 'geo' };
  }, [catalog, activeType, activeGroup, embedded, setupPhases, singleTypeMode]);

  const currentType = resolvedType.type;
  const currentGroup = resolvedType.group;

  const catalogGroupKeys = useMemo(() => catalog.map((g) => g.key).join('|'), [catalog]);

  useEffect(() => {
    if (singleTypeMode || !catalogGroupKeys) return;
    setOpenGroups(Object.fromEntries(catalogGroupKeys.split('|').map((k) => [k, true])));
  }, [catalogGroupKeys, singleTypeMode]);

  useEffect(() => {
    if (singleTypeMode) return;
    if (!resolvedType.type || !resolvedType.group) return;
    if (resolvedType.type === activeType && resolvedType.group === activeGroup) return;
    setSearchParams({ group: resolvedType.group, type: resolvedType.type }, { replace: true });
    const groupKey = resolvedType.group;
    setOpenGroups((prev) => (prev[groupKey] ? prev : { ...prev, [groupKey]: true }));
  }, [resolvedType.type, resolvedType.group, activeType, activeGroup, setSearchParams, embedded, catalogGroupKeys, singleTypeMode]);

  const isEmbeddedExternal = isEmbeddedExternalType(currentType)
    || isEmbeddedExternalMaster(currentGroup, currentType);
  const embeddedMeta = embeddedExternalMasterMeta(currentType);

  useEffect(() => {
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [currentType, debouncedSearch, stateCountryFilter, stateStatusFilter, cityStateFilter]);

  const isStatesType = currentType === 'states';
  const isCitiesType = currentType === 'cities';
  const isGeoChildType = isStatesType || isCitiesType;
  const isCurrenciesType = currentType === 'currencies';
  const isLanguagesType = currentType === 'languages';
  const isTimezonesType = currentType === 'timezones';
  const usageDeleteKind = isCitiesType
    ? 'city'
    : isStatesType ? 'state' : isCurrenciesType ? 'currency' : isTimezonesType ? 'timezone' : null;
  const listFilters: { country?: string; state?: string; status?: 'active' | 'inactive' } = isGeoChildType
    ? {
      country: stateCountryFilter || undefined,
      state: isCitiesType && stateCountryFilter ? cityStateFilter || undefined : undefined,
      status: stateStatusFilter === 'all' ? undefined : stateStatusFilter,
    }
    : {};

  const { data: schemaRaw, isError: schemaError, error: schemaFetchError, isLoading: schemaLoading } = useQuery({
    queryKey: ['master-schema', currentType],
    queryFn: () => getMasterSchema(currentType),
    enabled: !isEmbeddedExternal && !!currentType,
    staleTime: 5 * 60_000,
  });
  const schema = useMemo(
    () => {
      if (!schemaRaw) return schemaRaw;
      try {
        let metadataFields = enrichFoodImageMetadataFields(
          enrichMetadataFields(normalizeMetadataFields(schemaRaw.metadataFields), currentType),
          currentType,
        );
        if (currentType === 'vehicle_types') {
          metadataFields = enrichTransportVehicleMetadataFields(metadataFields);
        }
        if (currentType === 'vehicle_brands') {
          metadataFields = enrichTransportBrandMetadataFields(metadataFields);
        }
        if (currentType === 'vehicle_models') {
          metadataFields = enrichTransportModelMetadataFields(metadataFields);
        }
        if (currentType === 'vehicle_variants') {
          metadataFields = enrichTransportVariantMetadataFields(metadataFields);
        }
        if (currentType === 'ride_attribute_values') {
          metadataFields = enrichRideAttributeValueMetadataFields(metadataFields);
        }
        if (currentType === 'ride_tags') {
          metadataFields = enrichRideTagMetadataFields(metadataFields);
        }
        if (currentType === 'food_tags') {
          metadataFields = enrichFoodTagMetadataFields(metadataFields);
        }
        if (currentType === 'food_attributes') {
          metadataFields = enrichFoodAttributeMetadataFields(metadataFields);
        }
        if (currentType === 'food_attribute_values') {
          metadataFields = enrichFoodAttributeValueMetadataFields(metadataFields);
        }
        if (currentType === 'food_variants') {
          metadataFields = enrichFoodVariantMetadataFields(metadataFields);
        }
        if (currentType === 'addon_groups') {
          metadataFields = enrichAddonGroupMetadataFields(metadataFields);
        }
        if (currentType === 'food_addons') {
          metadataFields = enrichFoodAddonMetadataFields(metadataFields);
        }
        if (currentType === 'preparation_times') {
          metadataFields = enrichPreparationTimeMetadataFields(metadataFields);
        }
        if (currentType === 'food_units') {
          metadataFields = enrichFoodUnitMetadataFields(metadataFields);
        }
        if (currentType === 'food_tax_groups') {
          metadataFields = enrichFoodTaxGroupMetadataFields(metadataFields);
        }
        if (currentType === 'kitchen_stations') {
          metadataFields = enrichKitchenStationMetadataFields(metadataFields);
        }
        if (currentType === 'food_availability_schedules') {
          metadataFields = enrichFoodAvailabilityScheduleMetadataFields(metadataFields);
        }
        if (currentType === 'food_images') {
          metadataFields = enrichFoodImageMasterMetadataFields(metadataFields);
        }
        if (currentType === 'delivery_slots') {
          metadataFields = enrichDeliverySlotMetadataFields(metadataFields);
        }
        if (currentType === 'packaging_types') {
          metadataFields = enrichPackagingTypeMetadataFields(metadataFields);
        }
        if (currentType === 'grocery_store_types') {
          metadataFields = enrichGroceryStoreTypeMetadataFields(metadataFields);
        }
        if (currentType === 'grocery_categories') {
          metadataFields = enrichGroceryCategoryMetadataFields(metadataFields);
        }
        if (currentType === 'grocery_sub_categories') {
          metadataFields = enrichGrocerySubCategoryMetadataFields(metadataFields);
        }
        if (currentType === 'grocery_brands') {
          metadataFields = enrichGroceryBrandMetadataFields(metadataFields);
        }
        if (currentType === 'warehouses') {
          metadataFields = enrichWarehouseMetadataFields(metadataFields);
        }
        if (currentType === 'product_variants') {
          metadataFields = enrichProductVariantMetadataFields(metadataFields);
        }
        if (currentType === 'weight_types') {
          metadataFields = enrichWeightTypeMetadataFields(metadataFields);
        }
        if (currentType === 'suppliers') {
          metadataFields = enrichSupplierMetadataFields(metadataFields);
        }
        if (currentType === 'stock_alerts') {
          metadataFields = enrichStockAlertMetadataFields(metadataFields);
        }
        if (currentType === 'grocery_tax_groups') {
          metadataFields = enrichGroceryTaxGroupMetadataFields(metadataFields);
        }
        if (currentType === 'grocery_product_images') {
          metadataFields = enrichGroceryProductImageMetadataFields(metadataFields);
        }
        if (currentType === 'grocery_availability_schedules') {
          metadataFields = enrichGroceryAvailabilityScheduleMetadataFields(metadataFields);
        }
        if (currentType === 'delivery_partner_pricing') {
          metadataFields = enrichDeliveryPartnerPricingMetadataFields(metadataFields, serviceSlug);
        }
        if (currentType === 'delivery_incentives') {
          metadataFields = enrichDeliveryIncentiveMetadataFields(metadataFields, serviceSlug);
        }
        if (currentType === 'delivery_partners_tiers') {
          metadataFields = enrichDeliveryPartnerTierMetadataFields(metadataFields);
        }
        if (currentType === 'merchant_document_types') {
          metadataFields = enrichMerchantDocumentMetadataFields(metadataFields);
        }
        if (currentType === 'cancellation_reasons') {
          metadataFields = enrichCancellationReasonMetadataFields(metadataFields);
        }
        if (currentType === 'substitution_rules') {
          metadataFields = enrichSubstitutionRuleMetadataFields(metadataFields);
        }
        if (currentType === 'service_availability_schedules') {
          metadataFields = enrichServiceAvailabilityScheduleMetadataFields(metadataFields);
        }
        return { ...schemaRaw, metadataFields };
      } catch (err) {
        console.error('[MasterDataPage] schema enrichment failed', err);
        return { ...schemaRaw, metadataFields: [] };
      }
    },
    [schemaRaw, currentType, serviceSlug],
  );
  const rootsOnly = currentType === 'service_categories';
  const { data, isLoading } = useQuery({
    queryKey: [
      'masters',
      currentType,
      debouncedSearch,
      paginationModel.page,
      paginationModel.pageSize,
      rootsOnly,
      listFilters.country,
      listFilters.state,
      listFilters.status,
    ],
    queryFn: () => getMasters(currentType, {
      search: debouncedSearch || undefined,
      limit: paginationModel.pageSize,
      page: paginationModel.page + 1,
      rootsOnly: rootsOnly || undefined,
      ...listFilters,
      // Deprecated service categories are excluded by the API by default.
    }),
    enabled: !isEmbeddedExternal && !!currentType,
    staleTime: 30_000,
  });

  const totalRows = data?.meta?.total ?? 0;
  const prefetchDuplicateCheck = FOOD_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
    || FOOD_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
    || DELIVERY_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
    || ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
    || ON_DEMAND_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType);
  const { data: mastersForDuplicateCheck } = useQuery({
    queryKey: ['masters', currentType, 'duplicate-check'],
    queryFn: () => getMasters(currentType, { limit: 10000, page: 1, rootsOnly: rootsOnly || undefined }),
    enabled: (dialogOpen || prefetchDuplicateCheck) && !isEmbeddedExternal && !!currentType && !isGeoChildType,
    staleTime: 0,
  });
  const pageMasterRecords = useMemo(
    () => ((data?.data ?? []) as MasterRow[]).map(mapMasterRowForValidation),
    [data],
  );
  const duplicateCheckRecords = useMemo(
    () => ((mastersForDuplicateCheck?.data ?? []) as MasterRow[]).map(mapMasterRowForValidation),
    [mastersForDuplicateCheck],
  );
  const existingMasterRecords = useMemo(() => {
    if (!duplicateCheckRecords.length) return pageMasterRecords;
    const merged = new Map<string, MasterValidationRecord>();
    for (const row of pageMasterRecords) {
      if (row._id) merged.set(row._id, row);
    }
    for (const row of duplicateCheckRecords) {
      if (row._id) merged.set(row._id, row);
    }
    return [...merged.values()];
  }, [duplicateCheckRecords, pageMasterRecords]);
  const lockedSortOrder = useMemo(() => {
    if (currentType === 'food_sub_categories' || currentType === 'food_attribute_values') return null;
    if (
      FOOD_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
      || DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
      || ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
    ) {
      return getNextFoodMasterSortOrder(existingMasterRecords);
    }
    if (TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)) {
      return getNextSequentialSortOrder(totalRows);
    }
    return null;
  }, [currentType, totalRows, existingMasterRecords]);
  const parentMasterType = HIERARCHY_PARENT_TYPE[currentType];
  const { data: foodAttributesForOrder } = useQuery({
    queryKey: ['masters', 'food_attributes', 'sort-order'],
    queryFn: () => getMasters('food_attributes', { limit: 1000, page: 1 }),
    enabled: currentType === 'food_attribute_values',
    staleTime: 60_000,
  });
  const foodAttributeOrderMap = useMemo(
    () => buildFoodAttributeOrderMap((foodAttributesForOrder?.data ?? []) as MasterRow[]),
    [foodAttributesForOrder],
  );
  const { data: parentOptionsData } = useQuery({
    queryKey: ['master-parent-options', currentType, parentMasterType],
    queryFn: () => getMasterOptions(parentMasterType!, { parentType: parentMasterType }),
    enabled: !!parentMasterType && !!schema?.supportsHierarchy,
  });
  const { data: addonGroupOptionsData } = useQuery({
    queryKey: ['master-options', 'addon_groups', 'food-addon-form'],
    queryFn: () => getMasterOptions('addon_groups'),
    enabled: dialogOpen && currentType === 'food_addons',
    staleTime: 60_000,
  });
  const validRefCodes = useMemo(
    () => (currentType === 'food_addons'
      ? { addon_groups: (addonGroupOptionsData ?? []).map((row) => row.code) }
      : undefined),
    [currentType, addonGroupOptionsData],
  );
  const parentOptions = useMemo(() => {
    const all = parentOptionsData ?? EMPTY_PARENT_OPTIONS;
    // Primary→sub: only offer root (no parent) categories as parents
    if (currentType === 'service_categories' && parentMasterType === 'service_categories') {
      return all.filter((p) => {
        const row = p as { parentId?: string | null };
        return !row.parentId;
      });
    }
    if (currentType === 'food_attribute_values') {
      return [...all].sort((a, b) => {
        const orderA = foodAttributeOrderMap.get(a.code.trim().toLowerCase()) ?? 9999;
        const orderB = foodAttributeOrderMap.get(b.code.trim().toLowerCase()) ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name);
      });
    }
    return all;
  }, [parentOptionsData, currentType, parentMasterType, foodAttributeOrderMap]);

  const activeTypeDef = useMemo(() => {
    if (isEmbeddedExternal && embeddedMeta) {
      const externalGroup = getEmbeddedExternalCatalogGroup({
        embedded: !!embedded,
        serviceHub,
        serviceSlug,
        enabledModules,
        isSuperAdmin,
      });
      return {
        group: catalog.find((g) => g.key === '_external') || externalGroup || catalog[0],
        type: {
          key: currentType,
          label: embeddedMeta.label,
          description: embeddedMeta.description,
          supportsHierarchy: false,
          geoFields: [],
          hasMetadata: false,
        },
      };
    }
    for (const g of catalog) {
      const t = g.types.find((x) => x.key === currentType);
      if (t) return { group: g, type: t };
    }
    return null;
  }, [catalog, currentType, embeddedMeta, isEmbeddedExternal, embedded, serviceHub, serviceSlug, enabledModules, isSuperAdmin]);

  useEffect(() => {
    if (!dialogOpen) {
      countryFormInitKey.current = null;
      return;
    }
    if (currentType === 'countries' || currentType === 'states' || currentType === 'cities' || currentType === 'currencies' || currentType === 'languages' || currentType === 'timezones') {
      const key = editing?._id || 'new';
      if (countryFormInitKey.current === key) return;
      countryFormInitKey.current = key;
    }
    if (editing) {
      const meta: Record<string, string | number | boolean> = {};
      Object.entries(editing.metadata || {}).forEach(([k, v]) => {
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') meta[k] = v;
      });
      const isActive = editing.isActive !== false;
      if (schema?.metadataFields?.some((f) => f.key === 'status') && meta.status === undefined) {
        meta.status = isActive ? 'active' : 'inactive';
      }
      if (currentType === 'vehicle_types' && !meta.serviceTypes && meta.serviceType) {
        meta.serviceTypes = String(meta.serviceType);
        delete meta.serviceType;
      }
      if (currentType === 'service_availability_schedules' || currentType === 'food_availability_schedules') {
        Object.assign(meta, normalizeServiceAvailabilityDaysMetadata(meta));
      }
      if (currentType === 'food_sub_categories') {
        const desc = String(meta.description ?? editing.description ?? '').trim();
        if (desc) meta.description = desc;
      }
      if (currentType === 'grocery_categories') {
        const desc = String(editing.description ?? meta.description ?? '').trim();
        if (desc) {
          // Prefer top-level Description field; keep metadata in sync on save.
          meta.description = desc;
        }
      }
      if (currentType === 'suppliers') {
        const rawPhone = String(meta.phone ?? '').trim();
        if (rawPhone) {
          const dialCodes = getCachedCountryDialCodes().map((o) => o.dialCode);
          const split = splitPhoneByDialCodes(rawPhone, dialCodes);
          if (split.dialCode) {
            meta.phoneCountryCode = split.dialCode;
            meta.phone = split.localNumber;
          } else if (!meta.phoneCountryCode) {
            meta.phoneCountryCode = '+91';
          }
        } else if (!meta.phoneCountryCode) {
          meta.phoneCountryCode = '+91';
        }
      }
      setForm({
        code: editing.code,
        name: editing.name,
        description: currentType === 'grocery_categories'
          ? (editing.description || String(meta.description ?? '') || '')
          : (editing.description || ''),
        country: editing.country || (currentType === 'warehouses' ? 'IN' : ''),
        state: editing.state || '',
        city: editing.city || '',
        district: editing.district || '',
        parentId: resolveParentId(editing, parentOptions, currentType),
        sortOrder: editing.sortOrder || Number(meta.sortOrder) || 0,
        isActive,
        metadata: meta,
      });
    } else {
      setForm(buildCreateFormState(currentType, schema, totalRows, existingMasterRecords, serviceSlug));
    }
  }, [editing, dialogOpen, parentOptions, currentType, schema, totalRows, existingMasterRecords, serviceSlug]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const metadata = { ...form.metadata };
      if (currentType === 'languages' && metadata.autoTranslate === undefined) {
        metadata.autoTranslate = true;
      }
      if (currentType === 'service_availability_schedules' || currentType === 'food_availability_schedules') {
        Object.assign(metadata, syncServiceAvailabilityDaysForSave(metadata));
      }
      if (currentType === 'restaurant_opening_hours' && metadata.isClosed) {
        metadata.openTime = '';
        metadata.closeTime = '';
      }
      if (currentType === 'food_sub_categories') {
        const desc = String(metadata.description ?? form.description ?? '').trim();
        if (desc) {
          metadata.description = desc;
        } else {
          delete metadata.description;
        }
      }
      if (currentType === 'food_variants') {
        metadata.foodCode = form.code.trim();
      }
      if (currentType === 'addon_groups') {
        metadata.minSelection = Number(metadata.minSelection ?? 0);
        metadata.maxSelection = Number(metadata.maxSelection ?? 1);
        metadata.required = metadata.required === true || metadata.required === 'true';
      }
      if (currentType === 'suppliers') {
        const dial = String(metadata.phoneCountryCode ?? '').trim();
        const local = String(metadata.phone ?? '').trim();
        if (local) {
          metadata.phone = composeE164(dial || '+91', local);
        } else {
          delete metadata.phone;
        }
        delete metadata.phoneCountryCode;
      }
      if (currentType === 'food_addons' && editing) {
        const priorImage = String(editing.metadata?.imageUrl ?? '').trim();
        const nextImage = String(metadata.imageUrl ?? '').trim();
        if (!nextImage && priorImage) {
          metadata.imageUrl = priorImage;
        }
      }
      if (currentType === 'cancellation_reasons') {
        const applies = String(metadata.appliesTo ?? '').trim().toLowerCase();
        if (applies === 'driver') metadata.appliesTo = 'partner';
      }
      let recordCode = form.code;
      let recordName = form.name;
      if (currentType === 'preparation_times') {
        const minutes = Number(metadata.minutes);
        if (Number.isFinite(minutes) && minutes >= 1) {
          recordName = formatPreparationTimeName(minutes);
          if (!editing) {
            recordCode = preparationTimeCodeFromMinutes(minutes);
          }
        }
      }
      if (currentType === 'food_units') {
        metadata.unitType = normalizeFoodUnitType(metadata.unitType);
      }
    if (currentType === 'food_tax_groups' || currentType === 'grocery_tax_groups') {
      const taxPercent = Number(metadata.taxPercent);
      if (Number.isFinite(taxPercent)) {
        recordName = formatFoodTaxGroupName(taxPercent);
      }
    }
      if (currentType === 'kitchen_stations') {
        metadata.stationType = normalizeKitchenStationType(metadata.stationType);
      }
      let parentId = form.parentId;
      const legacyKey = LEGACY_PARENT_META_KEY[currentType];
      if (legacyKey) {
        const metaCode = String(metadata[legacyKey] || '');
        if (metaCode && !parentId) {
          const parent = parentOptions.find((p) => p.code === metaCode);
          if (parent) parentId = parent._id;
        }
      }
      if (parentId) {
        const parent = parentOptions.find((p) => p._id === parentId);
        if (parent && legacyKey) {
          metadata[legacyKey] = parent.code;
        }
      }
      // Keep metadata.status in sync with record active flag
      if (
        schema?.metadataFields?.some((f) => f.key === 'status')
        || TRANSPORT_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
        || FOOD_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
        || DELIVERY_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
      ) {
        metadata.status = form.isActive === false ? 'inactive' : 'active';
      }
      if (currentType === 'grocery_categories') {
        const desc = String(form.description ?? '').trim();
        if (desc) metadata.description = desc;
        else delete metadata.description;
      }
      let recordSortOrder = form.sortOrder;
      if (
        !editing
        && (
          (FOOD_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
            && currentType !== 'food_sub_categories'
            && currentType !== 'food_attribute_values')
          || DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
          || ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
        )
        && lockedSortOrder != null
        && (
          !Number.isFinite(recordSortOrder)
          || recordSortOrder < 1
          || currentType === 'delivery_partners_tiers'
          || currentType === 'merchant_document_types'
          || currentType === 'cancellation_reasons'
          || currentType === 'substitution_rules'
          || currentType === 'grocery_store_types'
          || currentType === 'grocery_categories'
          || currentType === 'grocery_brands'
          || currentType === 'warehouses'
          || currentType === 'product_variants'
          || currentType === 'weight_types'
          || currentType === 'suppliers'
          || currentType === 'stock_alerts'
          || currentType === 'grocery_tax_groups'
          || currentType === 'grocery_product_images'
          || currentType === 'grocery_availability_schedules'
          || currentType === 'packaging_types'
          || currentType === 'delivery_slots'
          || currentType === 'delivery_partner_pricing'
          || currentType === 'service_categories'
        )
      ) {
        recordSortOrder = lockedSortOrder;
      }
      if (!editing && (
        currentType === 'grocery_categories'
        || currentType === 'grocery_store_types'
        || currentType === 'grocery_brands'
        || currentType === 'warehouses'
        || currentType === 'product_variants'
        || currentType === 'weight_types'
        || currentType === 'suppliers'
        || currentType === 'stock_alerts'
        || currentType === 'grocery_tax_groups'
        || currentType === 'grocery_product_images'
        || currentType === 'grocery_availability_schedules'
        || currentType === 'delivery_partner_pricing'
        || currentType === 'delivery_slots'
        || currentType === 'packaging_types'
        || ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
      )) {
        // Final guard — never persist sortOrder 0 / missing on create.
        if (!Number.isFinite(recordSortOrder) || recordSortOrder < 1) {
          recordSortOrder = getNextFoodMasterSortOrder(existingMasterRecords);
        }
      }
      if (!editing && currentType === 'grocery_sub_categories') {
        const parentCode = String(form.metadata.parentCategoryCode ?? '').trim();
        if (!parentCode) {
          throw new Error('Parent category is required');
        }
        if (!Number.isFinite(recordSortOrder) || recordSortOrder < 1) {
          recordSortOrder = getNextGrocerySubCategorySortOrder(existingMasterRecords, parentCode);
        }
      }
      // Always re-check uniqueness at save time (covers stale "next" values and edit collisions).
      if (
        DELIVERY_UNIQUE_SORT_ORDER_TYPES.has(currentType)
        || FOOD_UNIQUE_SORT_ORDER_TYPES.has(currentType)
        || TRANSPORT_UNIQUE_SORT_ORDER_TYPES.has(currentType)
        || ON_DEMAND_UNIQUE_SORT_ORDER_TYPES.has(currentType)
      ) {
        const taken = new Set(
          existingMasterRecords
            .filter((row) => !editing || row._id !== editing._id)
            .map((row) => resolveRecordSortOrder(row))
            .filter((n) => n >= 1),
        );
        if (taken.has(recordSortOrder)) {
          if (!editing && (
            DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
            || ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
          )) {
            recordSortOrder = getNextFoodMasterSortOrder(existingMasterRecords);
          } else {
            throw new Error(`Sort order ${recordSortOrder} is already used — choose a unique display order`);
          }
        }
      }
      if (
        DELIVERY_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType)
        || currentType === 'food_sub_categories'
        || ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType)
      ) {
        if (currentType === 'service_categories') {
          const parentId = String(form.parentId || '').trim();
          const siblings = existingMasterRecords.filter((row) => {
            if (editing && row._id === editing._id) return false;
            const rowParent = String(row.parentId || '').trim();
            return parentId ? rowParent === parentId : !rowParent;
          });
          const taken = new Set(
            siblings.map((row) => resolveRecordSortOrder(row)).filter((n) => n >= 1),
          );
          if (taken.has(recordSortOrder)) {
            if (!editing) {
              recordSortOrder = getNextFoodMasterSortOrder(siblings);
            } else {
              throw new Error(`Sort order ${recordSortOrder} is already used — choose a unique display order`);
            }
          }
        } else {
        const parentCode = String(form.metadata.parentCategoryCode ?? '').trim().toLowerCase();
        const siblings = existingMasterRecords.filter((row) => {
          if (editing && row._id === editing._id) return false;
          return resolveGrocerySubCategoryParentCode(row) === parentCode;
        });
        const taken = new Set(
          siblings.map((row) => resolveRecordSortOrder(row)).filter((n) => n >= 1),
        );
        if (taken.has(recordSortOrder)) {
          if (!editing && parentCode) {
            recordSortOrder = currentType === 'grocery_sub_categories'
              ? getNextGrocerySubCategorySortOrder(existingMasterRecords, parentCode)
              : getNextFoodMasterSortOrder(siblings);
          } else {
            throw new Error(`Sort order ${recordSortOrder} is already used under this parent category`);
          }
        }
        }
      }
      if (
        schema?.metadataFields?.some((f) => f.key === 'sortOrder')
        || DELIVERY_INTEGER_SORT_ORDER_TYPES.has(currentType)
        || FOOD_INTEGER_SORT_ORDER_TYPES.has(currentType)
      ) {
        metadata.sortOrder = recordSortOrder;
      }
      const payload: Record<string, unknown> = {
        code: recordCode,
        name: recordName,
        description: currentType === 'food_sub_categories'
          ? (String(metadata.description ?? form.description ?? '').trim() || undefined)
          : (form.description || undefined),
        country: form.country || (currentType === 'warehouses' ? 'IN' : undefined),
        state: form.state || undefined,
        city: form.city || undefined,
        district: form.district || undefined,
        sortOrder: recordSortOrder,
        isActive: form.isActive !== false,
        metadata,
      };
      if (parentId) payload.parentId = parentId;
      else if (currentType === 'service_categories') payload.parentId = null;
      if (editing) return updateMaster(editing._id, payload);
      return createMaster(currentType, payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['masters', currentType] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', currentType] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      queryClient.invalidateQueries({ queryKey: ['masters', currentType, 'duplicate-check'] });
      if (isLanguagesType) {
        queryClient.invalidateQueries({ queryKey: ['locale-languages'] });
        queryClient.invalidateQueries({ queryKey: ['translations'] });
        void reloadLanguages();
        const translating = !editing && form.metadata.autoTranslate !== false && form.code.trim().toLowerCase() !== 'en';
        setSaveToast(
          `Language "${form.name.trim()}" ${editing ? 'updated' : 'created'} successfully${translating
            ? ' — translating strings in the background; untranslated text shows in English meanwhile'
            : ''}`,
        );
      }
      if (currentType === 'countries' || isGeoChildType || isCurrenciesType || isTimezonesType || currentType === 'address_fields' || currentType === 'payment_methods' || currentType === 'banks') {
        const noun = isCitiesType
          ? 'City'
          : isStatesType ? 'State / Province' : isCurrenciesType ? 'Currency' : isTimezonesType ? 'Timezone'
            : currentType === 'address_fields' ? 'Address field'
              : currentType === 'payment_methods' ? 'Payment method'
                : currentType === 'banks' ? 'Bank' : 'Country';
        const savedInactive = currentType === 'payment_methods' && !editing
          && (saved as { isActive?: boolean } | undefined)?.isActive === false;
        setSaveToast(`${noun} "${form.name.trim()}" ${editing ? 'updated' : 'created'} successfully${savedInactive
          ? ' — saved as Inactive until its gateway is configured'
          : ''}`);
      } else if (!isLanguagesType) {
        const savedName = String((saved as { name?: string } | undefined)?.name || form.name).trim();
        setSaveToast(`${schema?.label || 'Record'} "${savedName}" ${editing ? 'updated' : 'created'} successfully`);
      }
      setDialogOpen(false);
      setEditing(null);
      setSaveError(null);
    },
    onError: (err: unknown) => {
      const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || '';
      const errMsg = err instanceof Error ? err.message : '';
      const msg = axiosMsg || errMsg;
      if (/duplicate|E11000|already exists|already used/i.test(msg)) {
        setSaveError(msg || 'This value is already used by another record');
        return;
      }
      setSaveError(msg || 'Could not save record');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMaster,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masters', currentType] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', currentType] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      if (TRANSPORT_HARD_DELETE_TYPES.has(currentType)) {
        queryClient.invalidateQueries({ queryKey: ['masters', currentType, 'duplicate-check'] });
      }
      if (currentType === 'merchant_document_types') {
        setLangToast('Document type deactivated — status set to Inactive. Use the status toggle to reactivate.');
      }
      if (isLanguagesType) {
        queryClient.invalidateQueries({ queryKey: ['locale-languages'] });
        void reloadLanguages();
        setSaveToast(`Language "${deleteTarget?.name ?? ''}" deactivated — removed from the language switcher`);
      }
      setDeleteTarget(null);
      setDeleteError(null);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setDeleteError(msg || 'Could not delete record');
    },
  });

  const canDeleteRow = useCallback((row: MasterRow) => {
    if (currentType === 'trip_types') return canDeleteTripTypeMaster(row);
    if (currentType === 'currencies' && (row as { isBase?: boolean }).isBase) return false;
    if (currentType === 'languages' && ((row as { isDefault?: boolean }).isDefault || row.isActive === false)) return false;
    if ((currentType === 'address_fields' || currentType === 'payment_methods' || currentType === 'banks' || currentType === 'payout_cycles') && (row as { deleteLock?: string | null }).deleteLock) return false;
    if (currentType === 'merchant_document_types') {
      const inactive = row.isActive === false
        || String(row.metadata?.status ?? '').trim().toLowerCase() === 'inactive';
      // Already inactive — use the status toggle to reactivate instead of delete.
      if (inactive) return false;
    }
    return true;
  }, [currentType]);

  const requestDelete = useCallback((row: MasterRow) => {
    setDeleteError(null);
    setDeleteTarget(row);
  }, []);

  const supportsStatusToggle = isGeoChildType
    || MASTER_TYPES_WITH_STATUS_TOGGLE.has(currentType)
    || TRANSPORT_STATUS_TOGGLE_TYPES.has(currentType)
    || FOOD_LIST_STATUS_TOGGLE_TYPES.has(currentType)
    || FOOD_STATUS_TOGGLE_LIST_TYPES.has(currentType)
    || DELIVERY_STATUS_TOGGLE_LIST_TYPES.has(currentType)
    || !!schema?.metadataFields?.some((f) => f.key === 'status');

  const toggleActiveMutation = useMutation({
    mutationFn: ({ row, active }: { row: MasterRow; active: boolean }) => {
      const payload: Record<string, unknown> = {
        code: row.code,
        name: row.name,
        description: row.description,
        isActive: active,
        sortOrder: row.sortOrder,
      };
      if (currentType === 'transport_countries' || currentType === 'countries') {
        payload.metadata = { ...(row.metadata || {}), status: active };
      }
      if (
        TRANSPORT_STATUS_TOGGLE_TYPES.has(currentType)
        || FOOD_LIST_STATUS_TOGGLE_TYPES.has(currentType)
        || FOOD_STATUS_TOGGLE_LIST_TYPES.has(currentType)
        || DELIVERY_STATUS_TOGGLE_LIST_TYPES.has(currentType)
        || schema?.metadataFields?.some((f) => f.key === 'status')
        || currentType === 'service_categories'
      ) {
        payload.metadata = {
          ...(row.metadata || {}),
          status: active ? 'active' : 'inactive',
        };
      }
      return updateMaster(row._id, payload);
    },
    onMutate: ({ row }) => setTogglingRowId(row._id),
    onSettled: () => setTogglingRowId(null),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['masters', currentType] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', currentType] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
      if (isLanguagesType) {
        queryClient.invalidateQueries({ queryKey: ['locale-languages'] });
        queryClient.invalidateQueries({ queryKey: ['translations'] });
        void reloadLanguages();
        const meta = (vars.row.metadata || {}) as Record<string, unknown>;
        const name = vars.row.name || vars.row.code;
        if (!vars.active) {
          setSaveToast(`"${name}" deactivated — removed from the language switcher`);
        } else if (meta.provisioned || String(vars.row.code).toLowerCase() === 'en') {
          setSaveToast(`"${name}" activated — now available in the language switcher`);
        } else if (meta.autoTranslate !== false) {
          setLangToast(`"${name}" activated — translating strings in the background. Until then users see English text.`);
        } else {
          setLangToast(`"${name}" activated without translations — users who pick it see English text. Turn on auto-translate or add strings in Translator Master.`);
        }
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err instanceof Error ? err.message : '')
        || 'Could not update status';
      setDeleteError(msg);
    },
  });

  const handleToggleActive = useCallback(
    (row: MasterRow, active: boolean) => {
      if (active && isMasterRecordDeprecated(row)) return;
      toggleActiveMutation.mutate({ row, active });
    },
    [toggleActiveMutation],
  );

  const importMutation = useMutation({
    mutationFn: (rows: MasterCsvRow[]) => bulkImportMasters(
      currentType,
      rows.map((r) => masterCsvRowToPayload(r)),
    ),
    onSuccess: (result) => {
      setImportResult(result);
      queryClient.invalidateQueries({ queryKey: ['masters', currentType] });
      queryClient.invalidateQueries({ queryKey: ['masters-count', currentType] });
      queryClient.invalidateQueries({ queryKey: ['master-options'] });
    },
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getMasters(currentType, {
        search: debouncedSearch || undefined,
        limit: 10000,
        page: 1,
        rootsOnly: rootsOnly || undefined,
        ...listFilters,
      });
      const exportRows = (res.data || []) as MasterRow[];
      downloadMasterCsv(exportRows as unknown as Record<string, unknown>[], currentType, schema);
    } finally {
      setExporting(false);
    }
  };

  const handleImportOpen = () => {
    setImportResult(null);
    setImportOpen(true);
  };

  const handleImport = (rows: MasterCsvRow[]) => {
    importMutation.mutate(rows);
  };

  const selectType = useCallback((groupKey: string, typeKey: string) => {
    setSearchParams({ group: groupKey, type: typeKey });
    setCatalogOpen(false);
    setOpenGroups((prev) => ({ ...prev, [groupKey]: true }));
  }, [setSearchParams]);

  const toggleGroup = useCallback((key: string) => {
    setOpenGroups((p) => ({ ...p, [key]: !p[key] }));
  }, []);

  const totalTypes = catalog.reduce((n, g) => n + g.types.length, 0);
  const rows = useMemo(() => {
    const raw = Array.isArray(data?.data) ? (data.data as MasterRow[]) : [];
    if (currentType === 'service_categories') return sortFoodMasterRowsBySortOrder(raw);
    if (currentType === 'skills'
      || currentType === 'certificates'
      || currentType === 'experience_levels'
      || currentType === 'pricing_templates'
      || currentType === 'visit_charges'
      || currentType === 'cancellation_reasons') {
      return sortFoodMasterRowsBySortOrder(raw);
    }
    if (currentType === 'restaurant_opening_hours') return sortRowsByOpeningHourDay(raw);
    if (currentType === 'food_sub_categories') return sortFoodSubCategoryRows(raw);
    if (currentType === 'food_attribute_values') return sortFoodAttributeValueRows(raw, foodAttributeOrderMap);
    if (currentType === 'food_availability_schedules') {
      return sortFoodAvailabilityScheduleRows(raw);
    }
    if (currentType === 'delivery_slots') {
      return sortDeliverySlotRows(raw);
    }
    if (currentType === 'packaging_types') {
      return sortPackagingTypeRows(raw);
    }
    if (currentType === 'grocery_store_types') {
      return sortGroceryStoreTypeRows(raw);
    }
    if (currentType === 'grocery_categories') {
      return sortGroceryCategoryRows(raw);
    }
    if (currentType === 'grocery_sub_categories') {
      return sortGrocerySubCategoryRows(raw);
    }
    if (currentType === 'grocery_brands') {
      return sortGroceryBrandRows(raw);
    }
    if (currentType === 'warehouses') {
      return sortWarehouseRows(raw);
    }
    if (currentType === 'product_variants') {
      return sortProductVariantRows(raw);
    }
    if (currentType === 'weight_types') {
      return sortWeightTypeRows(raw);
    }
    if (currentType === 'suppliers') {
      return sortSupplierRows(raw);
    }
    if (currentType === 'stock_alerts') {
      return sortStockAlertRows(raw);
    }
    if (currentType === 'grocery_tax_groups') {
      return sortGroceryTaxGroupRows(raw);
    }
    if (currentType === 'grocery_product_images') {
      return sortGroceryProductImageRows(raw);
    }
    if (currentType === 'grocery_availability_schedules') {
      return sortGroceryAvailabilityScheduleRows(raw);
    }
    if (currentType === 'delivery_partner_pricing') {
      return sortDeliveryPartnerPricingRows(raw);
    }
    if (currentType === 'delivery_incentives') {
      return sortDeliveryIncentiveRows(raw);
    }
    if (currentType === 'delivery_partners_tiers') {
      return sortDeliveryPartnerTierRows(raw);
    }
    if (currentType === 'merchant_document_types') {
      return sortMerchantDocumentRows(raw);
    }
    if (currentType === 'cancellation_reasons') {
      return sortCancellationReasonRows(raw);
    }
    if (currentType === 'substitution_rules') {
      return sortSubstitutionRuleRows(raw);
    }
    if (
      currentType === 'porter_special_handling'
      || currentType === 'porter_cancellation_reasons'
      || currentType === 'porter_document_types'
      || currentType === 'addon_groups'
      || currentType === 'food_addons'
      || currentType === 'preparation_times'
      || currentType === 'food_units'
      || currentType === 'food_tax_groups'
      || currentType === 'kitchen_stations'
      || currentType === 'food_images'
    ) {
      return sortFoodMasterRowsBySortOrder(raw);
    }
    return raw;
  }, [data, currentType, foodAttributeOrderMap]);

  const catalogProps = {
    catalog,
    currentGroup,
    currentType,
    openGroups,
    onToggleGroup: toggleGroup,
    onSelectType: selectType,
    totalTypes,
    accordionMode: false,
  };

  const openCreate = () => {
    if (isEmbeddedExternal && currentType === 'partner_subscription_plans') {
      setPartnerPlanAddRequest((n) => n + 1);
      return;
    }
    if (isEmbeddedExternal && currentType === 'restaurants') {
      setRestaurantAddRequest((n) => n + 1);
      return;
    }
    setEditing(null);
    setSaveError(null);
    setForm(buildCreateFormState(currentType, schema, totalRows, existingMasterRecords, serviceSlug));
    setDialogOpen(true);
  };
  const openEdit = (row: MasterRow) => {
    setEditing(row);
    setSaveError(null);
    setDialogOpen(true);
  };

  const typeHelper = ON_DEMAND_MASTER_HELPERS[currentType as OnDemandMasterTypeKey];
  const headerDescription = typeHelper
    || embeddedMeta?.description
    || schema?.description
    || activeTypeDef?.type.description;

  const statusToggleOnly = FOOD_STATUS_TOGGLE_LIST_TYPES.has(currentType)
    || DELIVERY_STATUS_TOGGLE_LIST_TYPES.has(currentType);

  const content = (
    <Box sx={{ minHeight: embedded ? undefined : '100%', minWidth: 0 }}>
      {!singleTypeMode && !embedded && (
        <MasterCatalogDrawer open={catalogOpen} onClose={() => setCatalogOpen(false)} {...catalogProps} />
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          minWidth: 0,
          minHeight: embedded ? undefined : 'calc(100vh - 96px)',
          ...(!embedded && !singleTypeMode && {
            height: { lg: 'calc(100dvh - 114px)' },
            minHeight: { xs: 'calc(100vh - 96px)', lg: 480 },
          }),
          borderRadius: embedded ? 0 : 2.5,
          border: embedded ? 0 : 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {!singleTypeMode && !embedded && (
          <MasterCatalogSidebar {...catalogProps} />
        )}

        <Box
          flex={1}
          minWidth={0}
          width="100%"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            ...(!embedded && !singleTypeMode && { overflowY: { lg: 'auto' } }),
          }}
        >
          <MasterTypeHeader
            title={embeddedMeta?.label || schema?.label || currentType.replace(/_/g, ' ')}
            description={headerDescription}
            groupLabel={embedded || singleTypeMode ? undefined : activeTypeDef?.group.label}
            recordCount={isEmbeddedExternal ? undefined : rows.length}
            totalCount={isEmbeddedExternal ? undefined : totalRows}
            search={isEmbeddedExternal ? undefined : search}
            onSearchChange={isEmbeddedExternal ? undefined : setSearch}
            searchPlaceholder={isStatesType
              ? 'Search name or code (e.g. IN-KA)'
              : isCitiesType
                ? 'Search city name or code'
                : isCurrenciesType
                  ? 'Search name, code, symbol or ISO number'
                  : isLanguagesType
                    ? 'Search language name or code'
                    : isTimezonesType
                      ? 'Search city, IANA ID, IST, IN or +05:30'
                      : currentType === 'address_fields'
                        ? 'Search field name or key'
                        : undefined}
            onAdd={openCreate}
            addLabel={
              isEmbeddedExternal && currentType === 'partner_subscription_plans'
                ? 'Add plan'
                : isEmbeddedExternal && currentType === 'restaurants'
                  ? 'Add Restaurant'
                  : currentType === 'service_categories'
                    ? 'Add primary'
                    : 'Add'
            }
            addHint={
              currentType === 'service_categories'
                ? 'Requires name, code, icon/image, display order, and accent color. Use the eye icon on a row to add secondary (sub) categories.'
                : undefined
            }
            onExport={isEmbeddedExternal ? undefined : handleExport}
            onImport={isEmbeddedExternal ? undefined : handleImportOpen}
            exportLoading={exporting}
            catalogTrigger={singleTypeMode || embedded ? undefined : <MasterCatalogMobileTrigger onClick={() => setCatalogOpen(true)} />}
          />

          <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1.5, md: 2 } }}>
            {schemaError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                Could not load master schema
                {(schemaFetchError as { message?: string })?.message
                  ? `: ${(schemaFetchError as { message?: string }).message}`
                  : '.'}
              </Alert>
            )}
            <MasterContentErrorBoundary resetKey={currentType}>
            {isEmbeddedExternal && currentType === 'partner_subscription_plans' ? (
              <PartnerSubscriptionPlanManager
                showHeader={false}
                addPlanRequestId={partnerPlanAddRequest}
              />
            ) : isEmbeddedExternal && currentType === 'restaurants' ? (
              <RestaurantsPage showHeader={false} addRequestId={restaurantAddRequest} />
            ) : !currentType ? (
              <Alert severity="info">
                No master type is selected. Choose a type from the sidebar, or confirm the Food module is enabled for this tenant.
              </Alert>
            ) : (
              <>
              {isGeoChildType && (
                <StateListFilters
                  country={stateCountryFilter}
                  status={stateStatusFilter}
                  onCountryChange={setStateCountryFilter}
                  onStatusChange={setStateStatusFilter}
                  state={isCitiesType ? cityStateFilter : undefined}
                  onStateChange={isCitiesType ? setCityStateFilter : undefined}
                />
              )}
              <MasterRecordTable
                rows={rows}
                schema={schema}
                masterTypeKey={currentType}
                loading={isLoading}
                totalCount={totalRows}
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                onPageChange={(page) => setPaginationModel((p) => ({ ...p, page }))}
                onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
                onEdit={openEdit}
                onDelete={requestDelete}
                canDelete={canDeleteRow}
                onView={currentType === 'service_categories' ? setCategoryChildrenParent : undefined}
                viewTooltip="Manage secondary categories"
                onToggleActive={supportsStatusToggle ? handleToggleActive : undefined}
                togglingRowId={supportsStatusToggle ? togglingRowId : null}
                statusToggleOnly={statusToggleOnly}
                stickyPagination={isGeoChildType}
                emptyMessage={isGeoChildType && (stateCountryFilter || stateStatusFilter !== 'all' || debouncedSearch)
                  ? `No ${isCitiesType ? 'cities' : 'states / provinces'} match the current filters`
                  : undefined}
              />
              </>
            )}
            </MasterContentErrorBoundary>
          </Box>
        </Box>
      </Box>

      {usageDeleteKind && (
        <StateDeleteDialog
          kind={usageDeleteKind}
          target={deleteTarget}
          loading={deleteMutation.isPending || toggleActiveMutation.isPending}
          error={deleteError}
          onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
          onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
          onDeactivate={() => {
            if (!deleteTarget) return;
            toggleActiveMutation.mutate(
              { row: deleteTarget, active: false },
              { onSuccess: () => { setDeleteTarget(null); setDeleteError(null); } },
            );
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget && !usageDeleteKind}
        title={currentType === 'merchant_document_types' ? 'Deactivate document type?' : 'Delete record?'}
        message={
          currentType === 'trip_types'
            ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the trip type from the list.`
            : currentType === 'vehicle_types'
              ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the vehicle type from the list.`
              : currentType === 'vehicle_brands'
                ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the brand from the list.`
                : currentType === 'vehicle_models'
                  ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the vehicle model from the list.`
                  : currentType === 'vehicle_variants'
                    ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the vehicle variant from the list.`
                    : currentType === 'ride_attributes'
                      ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the ride attribute from the list.`
                      : currentType === 'ride_attribute_values'
                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the ride attribute value from the list.`
                        : currentType === 'ride_tags'
                          ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the ride tag from the list.`
                          : currentType === 'service_availability_schedules'
                            ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the availability schedule from the list.`
                            : currentType === 'food_tax_groups'
                              ? `Deactivate "${deleteTarget?.name}" (${deleteTarget?.code})? Records assigned to menu items cannot be removed — use the status toggle to deactivate instead.`
                              : currentType === 'kitchen_stations'
                                ? `Deactivate "${deleteTarget?.name}" (${deleteTarget?.code})? Stations assigned to menu items cannot be removed — use the status toggle to deactivate instead.`
                                : currentType === 'food_availability_schedules'
                                  ? `Deactivate "${deleteTarget?.name}" (${deleteTarget?.code})? Schedules assigned to menu items cannot be removed — use the status toggle to deactivate instead.`
                                  : currentType === 'food_images'
                                    ? `Deactivate "${deleteTarget?.name}" (${deleteTarget?.code})? Images linked to menu items cannot be removed — use the status toggle to deactivate instead.`
                                    : currentType === 'packaging_types'
                                      ? `Deactivate "${deleteTarget?.name}" (${deleteTarget?.code})? Packaging types used in orders cannot be removed — use the status toggle to deactivate instead.`
                                      : currentType === 'service_categories'
                                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? This removes the category from the list. Sub-categories must be deleted first.`
                                      : currentType === 'skills'
                                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? Blocked if packages or provider profiles still use this skill.`
                                      : currentType === 'certificates'
                                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? Blocked for core KYC documents and when providers have uploaded or listed this certificate.`
                                      : currentType === 'pricing_templates'
                                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? Blocked if active service packages were priced from this template.`
                                      : currentType === 'cancellation_reasons'
                                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? Blocked if bookings, orders or rides were cancelled with this reason — deactivate it instead.`
                                      : currentType === 'visit_charges'
                                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? Blocked if active packages use this visit charge, or if it is the last active visit charge that packages fall back to.`
                                      : currentType === 'experience_levels'
                                        ? `Permanently delete "${deleteTarget?.name}" (${deleteTarget?.code})? Blocked if providers in this experience range would be left without a level.`
                                      : currentType === 'merchant_document_types'
                                        ? `Deactivate "${deleteTarget?.name}" (${deleteTarget?.code})? This does not permanently delete the document type — it sets status to Inactive. You can reactivate it later with the status toggle.`
                                      : `Remove "${deleteTarget?.name}" (${deleteTarget?.code})? This cannot be undone.`
        }
        confirmLabel={currentType === 'merchant_document_types' ? 'Deactivate' : 'Delete'}
        loading={deleteMutation.isPending}
        onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
      />

      <Snackbar
        open={!!deleteError && !(usageDeleteKind && deleteTarget)}
        autoHideDuration={7000}
        onClose={() => setDeleteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setDeleteError(null)} variant="filled" sx={{ width: '100%' }}>
          {deleteError}
        </Alert>
      </Snackbar>

      <CategoryChildrenDrawer
        open={!!categoryChildrenParent}
        parent={categoryChildrenParent}
        onClose={() => setCategoryChildrenParent(null)}
      />

      {currentType === 'countries' ? (
        <CountryFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          existingRecords={existingMasterRecords}
          editingId={editing?._id}
          saveError={saveError}
        />
      ) : currentType === 'currencies' ? (
        <CurrencyFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          editingId={editing?._id}
          saveError={saveError}
        />
      ) : currentType === 'timezones' ? (
        <TimezoneFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          editingId={editing?._id}
          saveError={saveError}
        />
      ) : currentType === 'banks' ? (
        <BankFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          editingId={editing?._id}
          saveError={saveError}
        />
      ) : currentType === 'payment_methods' ? (
        <PaymentMethodFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          editingId={editing?._id}
          saveError={saveError}
        />
      ) : currentType === 'address_fields' ? (
        <AddressFieldFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          editingId={editing?._id}
          saveError={saveError}
        />
      ) : currentType === 'languages' ? (
        <LanguageFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          editingId={editing?._id}
          saveError={saveError}
          onRetranslateStarted={setLangToast}
        />
      ) : currentType === 'cities' ? (
        <CityFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          editingId={editing?._id}
          saveError={saveError}
        />
      ) : currentType === 'states' ? (
        <StateFormDrawer
          open={dialogOpen}
          editing={!!editing}
          form={form}
          onChange={setForm}
          onClose={() => { setDialogOpen(false); setSaveError(null); }}
          onSubmit={() => saveMutation.mutate()}
          loading={saveMutation.isPending}
          editingId={editing?._id}
          saveError={saveError}
        />
      ) : (
      <MasterFormDrawer
        open={dialogOpen}
        editing={!!editing}
        schema={schema}
        schemaLoading={schemaLoading && !isEmbeddedExternal && !!currentType}
        currentType={currentType}
        form={form}
        onChange={setForm}
        onClose={() => { setDialogOpen(false); setSaveError(null); }}
        onSubmit={() => saveMutation.mutate()}
        loading={saveMutation.isPending}
        parentMasterType={parentMasterType}
        parentOptions={parentOptions}
        lockedSortOrder={!editing ? lockedSortOrder : null}
        existingRecords={existingMasterRecords}
        editingId={editing?._id}
        saveError={saveError}
        validRefCodes={validRefCodes}
      />
      )}

      <MasterImportDialog
        open={importOpen}
        onClose={() => { setImportOpen(false); setImportResult(null); }}
        masterType={currentType}
        masterLabel={schema?.label || currentType}
        schema={schema}
        loading={importMutation.isPending}
        onImport={handleImport}
        result={importResult}
      />

      <Snackbar
        open={!!langToast}
        autoHideDuration={7000}
        onClose={() => setLangToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setLangToast(null)} variant="filled" sx={{ width: '100%' }}>
          {langToast}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!saveToast}
        autoHideDuration={4000}
        onClose={() => setSaveToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSaveToast(null)} variant="filled" sx={{ width: '100%' }}>
          {saveToast}
        </Alert>
      </Snackbar>
    </Box>
  );

  if (embedded) return content;
  return <PageShell maxWidth={false}>{content}</PageShell>;
}
